/**
 * useAlignViewManagement
 *
 * Manages loading and caching of surgery views for the Align step.
 * Supports both single plan mode and comparison mode (two plans side by side).
 *
 * What it does:
 * 1. Loads surgery views from remote renderer (LoadSpinesimViewStage)
 * 2. Applies surgical state (decompensation + surgical acts)
 * 3. Caches views with planId in the cache key
 * 4. Returns cached views for rendering
 *
 * In comparison mode, it loads and caches views for both plans independently.
 */
import { useEffect, useMemo, useState } from "react";
import { MedicalImagesType } from "../../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../../classes/enums/PlanningStateEnum";
import {
  MapViewNameToSpinesimSurgeryViewType,
  SpinesimSurgeryViewType,
} from "../../../classes/enums/ViewEnum";
import { generateCacheKey } from "../../../classes/utils/RemoteRenderingUtils";

const useAlignViewManagement = ({
  selectedTwin,
  selectedModality,
  groupedThumbnailsStandard,
  alignSessionVersion,
  currentPlanId,
  digitalTwinSurgeryCache,
  setAreViewsReady,
  areViewsReady,
  twinId,
  renderClient,
  constructedImageData,
  allInstances,
  instances,
  loadingState,
  dataBundle,
  startSurgery,
  performDecompensation,
  setIsLoading,
  setGlobalAlignmentSelectionFirstTime,
  // Comparison mode params (optional)
  comparedPlanId = null,
  comparedSurgicalActs = null,
  comparedGlobalAlignmentAct = null,
}) => {
  const [areComparedViewsReady, setAreComparedViewsReady] = useState(false);
  useEffect(() => {
    const shouldRefreshData = allInstances.some(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.VALIDATE
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
      const views = Array.isArray(cachedData) ? cachedData : [cachedData];

      const validationViewTypes = [
        MedicalImagesType.LATERAL_VALIDATION_DX,
        MedicalImagesType.FRONT_VALIDATION_DX,
      ];

      const hasValidationViews = views.some(view =>
        validationViewTypes.includes(view.viewName)
      );

      if (hasValidationViews) {
        digitalTwinSurgeryCache.removeView(cacheKey);
      }

      return hasValidationViews;
    });

    const hasCorrectAlignViews = allInstances.some(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.ALIGN,
        currentPlanId
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
      const views = Array.isArray(cachedData) ? cachedData : [cachedData];

      const requiredAlignViewTypes = [
        MedicalImagesType.LATERAL_SURGERY_DX,
        MedicalImagesType.LATERAL_SUPPORT_SURGERY,
      ];

      return views.some(view => requiredAlignViewTypes.includes(view.viewName));
    });

    const shouldLoadSurgery =
      constructedImageData &&
      renderClient &&
      allInstances.length > 0 &&
      instances &&
      loadingState.plan &&
      loadingState.surgicalActs &&
      (!hasCorrectAlignViews || shouldRefreshData);

    if (shouldLoadSurgery) {
      setIsLoading(true);

      (async () => {
        try {
          const response = await renderClient.LoadSpinesimViewStage(
            currentPlanId,
            JSON.stringify(constructedImageData),
            PLANNING_STATES.ALIGN,
            shouldRefreshData
          );

          const surgeryResult = JSON.parse(response);
          const sagittalInstanceId =
            instances.sagittal_dx_instance_id?._id ||
            instances.sagittal_dx_instance_id;

          const alignViews = surgeryResult.filter(
            view =>
              view.viewName === MedicalImagesType.LATERAL_SURGERY_DX ||
              view.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
          );

          if (alignViews.length > 0) {
            const sagittalInstance = allInstances.find(
              inst => inst._id === sagittalInstanceId
            );

            if (sagittalInstance) {
              const sagittalCacheKey = generateCacheKey(
                sagittalInstance,
                twinId,
                PLANNING_STATES.ALIGN,
                currentPlanId
              );
              digitalTwinSurgeryCache.setView(sagittalCacheKey, alignViews);
            }
          }


          const globalAlignment = dataBundle.globalAlignmentAct?.[0];
          try {
            if (globalAlignment) {
              await performDecompensation(
                globalAlignment.pelvicTilt,
                globalAlignment.kneeAngle,
                globalAlignment.method,
                globalAlignment.lumbarLordosis,
                globalAlignment.sacralSlope,
                globalAlignment.globalTilt,
                globalAlignment.t1pa,
                globalAlignment.targetPelvicTilt,
                globalAlignment.piLl
              );

              const hasSurgicalActs =
                dataBundle.surgicalActs && dataBundle.surgicalActs.length > 0;

              if (hasSurgicalActs) {
                await startSurgery(dataBundle.surgicalActs);
              }
            } else {
              setGlobalAlignmentSelectionFirstTime();
            }
          } catch (decompensationError) {}

          setAreViewsReady(true);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      })();
    } else if (!shouldRefreshData && hasCorrectAlignViews) {
      setAreViewsReady(true);
    }
  }, [
    constructedImageData,
    renderClient,
    allInstances,
    instances,
    digitalTwinSurgeryCache,
    setAreViewsReady,
    twinId,
    performDecompensation,
    loadingState.plan,
    loadingState.surgicalActs,
    dataBundle.surgicalActs,
    dataBundle.globalAlignmentAct,
    startSurgery,
    currentPlanId,
    setGlobalAlignmentSelectionFirstTime,
    alignSessionVersion,
    setIsLoading,
  ]);

  // Effect for loading COMPARED plan views (only when in comparison mode)
  useEffect(() => {
    if (!comparedPlanId) {
      setAreComparedViewsReady(false);
      return;
    }

    const hasCorrectComparedAlignViews = allInstances.some(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.ALIGN,
        comparedPlanId
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
      const views = Array.isArray(cachedData) ? cachedData : [cachedData];

      const requiredAlignViewTypes = [
        MedicalImagesType.LATERAL_SURGERY_DX,
        MedicalImagesType.LATERAL_SUPPORT_SURGERY,
      ];

      return views.some(view => requiredAlignViewTypes.includes(view.viewName));
    });

    const shouldLoadComparedSurgery =
      constructedImageData &&
      renderClient &&
      allInstances.length > 0 &&
      instances &&
      comparedSurgicalActs !== null &&
      comparedGlobalAlignmentAct !== null &&
      !hasCorrectComparedAlignViews;

    if (shouldLoadComparedSurgery) {
      (async () => {
        try {
          const response = await renderClient.LoadSpinesimViewStage(
            comparedPlanId,
            JSON.stringify(constructedImageData),
            PLANNING_STATES.ALIGN,
            false
          );

          const surgeryResult = JSON.parse(response);
          const sagittalInstanceId =
            instances.sagittal_dx_instance_id?._id ||
            instances.sagittal_dx_instance_id;

          const alignViews = surgeryResult.filter(
            view =>
              view.viewName === MedicalImagesType.LATERAL_SURGERY_DX ||
              view.viewName === MedicalImagesType.LATERAL_SUPPORT_SURGERY
          );

          if (alignViews.length > 0) {
            const sagittalInstance = allInstances.find(
              inst => inst._id === sagittalInstanceId
            );

            if (sagittalInstance) {
              const sagittalCacheKey = generateCacheKey(
                sagittalInstance,
                twinId,
                PLANNING_STATES.ALIGN,
                comparedPlanId
              );
              digitalTwinSurgeryCache.setView(sagittalCacheKey, alignViews);
            }
          }

          // Apply compared plan's surgical state
          const comparedGlobalAlignment = comparedGlobalAlignmentAct?.[0];
          if (comparedGlobalAlignment && renderClient) {
            const defaultGlobalAlignmentData = {
              pelvicTilt: comparedGlobalAlignment.pelvicTilt,
              kneeAngle: comparedGlobalAlignment.kneeAngle,
              lumbarLordosis: comparedGlobalAlignment.lumbarLordosis,
              sacralSlope: comparedGlobalAlignment.sacralSlope,
              globalTilt: comparedGlobalAlignment.globalTilt,
              t1pa: comparedGlobalAlignment.t1pa,
              targetPelvicTilt: comparedGlobalAlignment.targetPelvicTilt,
              piLl: comparedGlobalAlignment.piLl,
              method: comparedGlobalAlignment.method,
            };

            await renderClient.StartDecompensation(
              comparedPlanId,
              MedicalImagesType.LATERAL_SURGERY_DX,
              defaultGlobalAlignmentData
            );

            if (comparedSurgicalActs && comparedSurgicalActs.length > 0) {
              await renderClient.StartSurgery(
                comparedPlanId,
                MapViewNameToSpinesimSurgeryViewType(
                  MedicalImagesType.LATERAL_SURGERY_DX
                ) || SpinesimSurgeryViewType.SAGITTAL,
                comparedSurgicalActs
              );
            }
          }

          setAreComparedViewsReady(true);
        } catch (error) {
          console.error(
            "[useAlignViewManagement] Error loading compared plan views:",
            error
          );
        }
      })();
    } else if (hasCorrectComparedAlignViews) {
      setAreComparedViewsReady(true);
    }
  }, [
    comparedPlanId,
    comparedSurgicalActs,
    comparedGlobalAlignmentAct,
    constructedImageData,
    renderClient,
    allInstances,
    instances,
    digitalTwinSurgeryCache,
    twinId,
    performDecompensation,
    startSurgery,
  ]);

  const currentModalityViews = useMemo(() => {
    if (!selectedTwin || !selectedModality || !areViewsReady) {
      return [];
    }

    const views = [];
    const modalityData = groupedThumbnailsStandard.find(
      item => item.modality === selectedModality
    );

    if (!modalityData) {
      return [];
    }

    const allowedViews = {
      DX: [
        MedicalImagesType.LATERAL_SURGERY_DX,
        MedicalImagesType.LATERAL_SUPPORT_SURGERY,
      ],
    };

    modalityData.instances.forEach(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.ALIGN,
        currentPlanId
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) {
        return;
      }

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);

      if (Array.isArray(cachedData)) {
        const filteredViews = cachedData.filter(view => {
          const isAllowed = allowedViews[selectedModality]?.includes(
            view.viewName
          );
          return isAllowed;
        });

        filteredViews.forEach((view, index) => {
          views.push({
            key: `${cacheKey}_${view.viewName}_${alignSessionVersion}`,
            viewData: view,
            instance,
            viewName: view.viewName,
            index: index,
          });
        });
      } else {
        const isAllowed = allowedViews[selectedModality]?.includes(
          cachedData.viewName
        );
        if (isAllowed) {
          views.push({
            key: `${cacheKey}_${alignSessionVersion}`,
            viewData: cachedData,
            instance,
            viewName: cachedData.viewName,
            index: 0,
          });
        }
      }
    });

    return views;
  }, [
    selectedTwin,
    selectedModality,
    areViewsReady,
    groupedThumbnailsStandard,
    twinId,
    currentPlanId,
    digitalTwinSurgeryCache,
    alignSessionVersion,
  ]);

  // Memoized views for COMPARED plan (only when in comparison mode)
  const comparedModalityViews = useMemo(() => {
    if (
      !selectedTwin ||
      !selectedModality ||
      !areComparedViewsReady ||
      !comparedPlanId
    ) {
      return [];
    }

    const views = [];
    const modalityData = groupedThumbnailsStandard.find(
      item => item.modality === selectedModality
    );

    if (!modalityData) {
      return [];
    }

    const allowedViews = {
      DX: [
        MedicalImagesType.LATERAL_SURGERY_DX,
        MedicalImagesType.LATERAL_SUPPORT_SURGERY,
      ],
    };

    modalityData.instances.forEach(instance => {
      const cacheKey = generateCacheKey(
        instance,
        twinId,
        PLANNING_STATES.ALIGN,
        comparedPlanId
      );
      if (!digitalTwinSurgeryCache.hasView(cacheKey)) {
        return;
      }

      const cachedData = digitalTwinSurgeryCache.getView(cacheKey);

      if (Array.isArray(cachedData)) {
        const filteredViews = cachedData.filter(view => {
          const isAllowed = allowedViews[selectedModality]?.includes(
            view.viewName
          );
          return isAllowed;
        });

        filteredViews.forEach((view, index) => {
          views.push({
            key: `${cacheKey}_${view.viewName}_${alignSessionVersion}_compared`,
            viewData: view,
            instance,
            viewName: view.viewName,
            index: index,
          });
        });
      } else {
        const isAllowed = allowedViews[selectedModality]?.includes(
          cachedData.viewName
        );
        if (isAllowed) {
          views.push({
            key: `${cacheKey}_${alignSessionVersion}_compared`,
            viewData: cachedData,
            instance,
            viewName: cachedData.viewName,
            index: 0,
          });
        }
      }
    });

    return views;
  }, [
    selectedTwin,
    selectedModality,
    areComparedViewsReady,
    comparedPlanId,
    groupedThumbnailsStandard,
    twinId,
    digitalTwinSurgeryCache,
    alignSessionVersion,
  ]);

  return {
    currentModalityViews,
    comparedModalityViews,
    areComparedViewsReady,
  };
};

export default useAlignViewManagement;
